import { Component, Input, OnInit, HostListener, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Trabajo } from '../../models/trabajo';
import { Metodosui } from '../../services/metodosui';
import { JavapiService } from '../../services/javapi.service';
import { Modelogeneraldatos } from '../../models/modelogeneraldatos';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { VerimagenComponent } from '../verimagen/verimagen.component';
import { GastosFormModalComponent } from './gastos-form-modal.component';
import { Browser } from '@capacitor/browser';

@Component({
  selector: 'app-gastosedit',
  templateUrl: './gastosedit.component.html',
  styleUrls: ['./gastosedit.component.scss'],
  standalone: false
})
export class GastoseditComponent implements OnInit {

  @Input() trabajo!: Trabajo;

  gastosList: any[] = [];
  loading = true;
  hasChanges: boolean = false; // Keep this, used for modal dismiss
  showSummaryDetails = false;

  //posGasto = ['Compra', 'Subcontrato', 'Combustible/Transporte', 'Planilla', 'Otro'];
  uniqueLugares: string[] = [];

  // Archivo preview (only isPdf is used by viewAttachment, others are obsolete)
  isPdf = false;

  // These are for the old inline form dropdowns, now obsolete
  showLugarDropdown = false;
  showTipoDropdown = false;
  filteredLugares: string[] = [];
  filteredTipos: string[] = [];

  constructor(
    private modalCtrl: ModalController,
    private metodosUi: Metodosui,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private sanitizer: DomSanitizer,
    private el: ElementRef
  ) { }

  ngOnInit() {
    this.loadGastos();
    //this.filteredTipos = [...this.tiposGasto];
  }


  async loadGastos() {
    if (!this.trabajo) return;
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.javapiService.getGastosList(String(this.trabajo.id)));
      this.gastosList = resp || [];
      // Populate unique locations for autocomplete
      const allLugares = this.gastosList.map((g: any) => g.dt4).filter(Boolean);
      this.uniqueLugares = Array.from(new Set(allLugares)).sort();
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error cargando gastos', 'danger');
    } finally {
      this.loading = false;
    }
  }

  totalGastosAmount(): number {
    return this.gastosList.reduce((acc, g) => acc + (parseFloat(g.dt5) || 0), 0);
  }

  getGastosBreakdown(): { tipo: string, total: number }[] {
    const totals: { [key: string]: number } = {};
    for (const g of this.gastosList) {
      const tipo = g.dt7 || 'Otro';
      const amt = parseFloat(g.dt5) || 0;
      totals[tipo] = (totals[tipo] || 0) + amt;
    }
    return Object.keys(totals).map(k => ({ tipo: k, total: totals[k] })).sort((a, b) => b.total - a.total);
  }

  getAvatarClass(tipo: string): string {
    if (!tipo) return '';
    const t = tipo.toLowerCase();
    if (t.includes('compra')) return 'compra';
    if (t.includes('plan')) return 'plan';
    if (t.includes('comb') || t.includes('transp')) return 'comb';
    if (t.includes('sub')) return 'sub';
    return '';
  }

  close() {
    this.modalCtrl.dismiss({ updated: this.hasChanges });
  }

  addNew() {
    this.presentFormModal(null);
  }

  editGasto(g: any) {
    const gastoToEdit = {
      id: g.dt1,
      fecha: g.dt3,
      lugar: g.dt4,
      monto: g.dt5,
      descripcion: g.dt6,
      tipo: g.dt7,
      archivoBase64: null,
      archivos: g.dt8
    };
    this.presentFormModal(gastoToEdit);
  }

  async presentFormModal(gasto: any | null) {
    const modal = await this.modalCtrl.create({
      component: GastosFormModalComponent,
      componentProps: {
        trabajoId: this.trabajo.id,
        gasto: gasto,
        uniqueLugares: this.uniqueLugares
      }
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data?.delete) {
      // Handle direct signal from modal if needed, though modal already closed.
      // The modal GastosFormModalComponent manages DELETE and returns.
      // But we need to refresh the list here.
      // Wait, let's see how we handled deletion in planilla.
      // If we emit { delete: true } from GastosFormModal, we should handle deletion here.

      // I'll update GastosFormModal to NOT delete but return a signal, 
      // or delete and return { success: true }.
      // Let's assume GastosFormModal returns { delete: true } and we handle it here
      // for better separation.

      this.performDeletion(gasto);
    } else if (data?.success) {
      this.hasChanges = true;
      this.loadGastos();
    }
  }

  async performDeletion(g: any) {
    try {
      this.loading = true;
      const userJson = localStorage.getItem('user');
      let usuario = 'system';
      if (userJson) {
        const user = JSON.parse(userJson);
        usuario = user.l_usuario || 'system';
      }

      await firstValueFrom(this.javapiService.deleteGasto(String(g.id), usuario));
      this.hasChanges = true;
      this.metodosUi.showToast('Gasto eliminado', 'success');
      this.loadGastos();
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error al eliminar', 'danger');
    } finally {
      this.loading = false;
    }
  }


  async viewAttachment(g: any) {
    if (!g.dt8) return;

    try {
      this.loading = true;
      // Handle multiple files if present, take the first one
      const rawFileName = String(g.dt8).split(',')[0].trim();
      const isPdf = /\.pdf$/i.test(rawFileName);

      if (isPdf) {
        // Para PDFs, abrimos con el plugin Browser de Capacitor para una mejor experiencia móvil
        const url = this.javapiService.getFileUrl('GAS', rawFileName);
        await Browser.open({ url: url });
        this.loading = false;
      } else {
        // Para imágenes, usamos el modal VerimagenComponent
        const imageData = await firstValueFrom(this.javapiService.getImage(rawFileName, 'path_img_trabajos_gastos'));
        this.loading = false;

        const modal = await this.modalCtrl.create({
          component: VerimagenComponent,
          componentProps: {
            image: imageData,
            title: 'Vista Previa de Comprobante'
          },
          cssClass: 'ver-imagen-modal'
        });
        await modal.present();
      }
    } catch (e) {
      console.error('Error viewing attachment:', e);
      this.loading = false;
      this.metodosUi.showToast('Error al cargar el archivo', 'danger');
    }
  }

}
